import { useEffect, useState } from "react";
import { Eye, History } from "lucide-react";

import AuditDetailDrawer from "@/components/AuditDetailDrawer";
import { api } from "@/lib/api";
import { formatBoliviaDateTime } from "@/lib/dateTime";

const eventLabels = {
  "reservation.created": "Reserva creada",
  "sale.confirmed": "Venta concretada",
  "lot.created": "Lote creado",
  "lot.imported": "Lote actualizado por Excel",
  "lot.released": "Lote liberado",
  "lot.reserved": "Lote reservado",
  "lot.sold": "Lote vendido",
  "lot.status_changed": "Estado de lote modificado",
  "lot.updated": "Datos de lote modificados",
};

function resourceName(event) {
  const data = event.after || event.before || {};
  const lot = data.lot || data;
  if (lot.manzano && lot.lote) return `Manzano ${lot.manzano} · Lote ${lot.lote}`;
  if (data.client_name) return data.client_name;
  return event.resource_id;
}

export default function OperationalAuditPanel({ category }) {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    api
      .get("/audit/events", { params: { category } })
      .then((response) => setEvents(response.data))
      .catch(() => setError("No fue posible cargar la trazabilidad operativa."))
      .finally(() => setLoading(false));
  }, [category]);

  return (
    <section className="audit-table-panel" data-testid="operational-audit-panel">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">TRAZABILIDAD OPERATIVA</span>
          <h2>{category === "reservations" ? "Reservas y ventas" : "Cambios de lotes"}</h2>
        </div>
        <History size={20} />
      </div>
      {error && <p className="form-error" data-testid="operational-audit-error">{error}</p>}
      {loading ? (
        <p data-testid="operational-audit-loading">Cargando trazabilidad...</p>
      ) : (
        <div className="data-table-wrap">
          <table data-testid="operational-audit-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Acción</th>
                <th>Recurso</th>
                <th>Responsable</th>
                <th>Origen</th>
                <th>Detalle</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr data-testid={`operational-audit-row-${event.id}`} key={event.id}>
                  <td>{formatBoliviaDateTime(event.created_at)}</td>
                  <td>{eventLabels[event.event_type] || event.event_type}</td>
                  <td>{resourceName(event)}</td>
                  <td>
                    <strong>{event.actor_name}</strong>
                    <small>{event.actor_role}</small>
                  </td>
                  <td>{event.source}</td>
                  <td>
                    <button
                      aria-label={`Ver detalle de auditoría ${event.id}`}
                      className="icon-button audit-view-button"
                      data-testid={`view-operational-audit-${event.id}`}
                      onClick={() => setSelectedEvent(event)}
                      type="button"
                    >
                      <Eye size={17} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!events.length && (
            <p className="empty-inline" data-testid="operational-audit-empty-state">
              Aún no hay eventos para este historial.
            </p>
          )}
        </div>
      )}
      {selectedEvent && (
        <AuditDetailDrawer
          detail={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          title={eventLabels[selectedEvent.event_type] || selectedEvent.event_type}
        />
      )}
    </section>
  );
}